import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaFolder } from 'react-icons/fa';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'portal-documents-help';

const DOC_COLOR_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'blue', label: 'Blue' },
];

const emptyDocCategory = (order) => ({ title: '', color: 'primary', bullets: [''], isActive: true, order });

const defaultForm = () => ({
  isPublished: true,
  title: 'Documents',
  icon: 'FaFolder',
  intro: '',
  categories: [],
});

const HelpDocumentsSectionCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          isPublished: res.data.data.isPublished !== false,
          title: c.title || '',
          icon: c.icon || 'FaFolder',
          intro: c.intro || '',
          categories: (c.categories || []).map((item, i) => ({ ...item, order: item.order ?? i })),
        });
      }
    } catch {
      toastError('Failed to load documents section');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const moveListItem = (index, dir) => {
    setForm((prev) => {
      const list = [...prev.categories];
      const next = index + dir;
      if (next < 0 || next >= list.length) return prev;
      [list[index], list[next]] = [list[next], list[index]];
      return { ...prev, categories: list.map((item, i) => ({ ...item, order: i })) };
    });
  };

  const updateList = (index, field, value) => {
    setForm((prev) => {
      const list = [...prev.categories];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, categories: list };
    });
  };

  const addCategory = () => setForm((prev) => ({ ...prev, categories: [...prev.categories, emptyDocCategory(prev.categories.length)] }));

  const removeCategory = (index) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i })),
    }));
  };

  const updateBullet = (catIndex, bulletIndex, value) => {
    setForm((prev) => {
      const categories = [...prev.categories];
      const bullets = [...(categories[catIndex].bullets || [])];
      bullets[bulletIndex] = value;
      categories[catIndex] = { ...categories[catIndex], bullets };
      return { ...prev, categories };
    });
  };

  const addBullet = (catIndex) => {
    setForm((prev) => {
      const categories = [...prev.categories];
      categories[catIndex] = {
        ...categories[catIndex],
        bullets: [...(categories[catIndex].bullets || []), ''],
      };
      return { ...prev, categories };
    });
  };

  const removeBullet = (catIndex, bulletIndex) => {
    setForm((prev) => {
      const categories = [...prev.categories];
      categories[catIndex] = {
        ...categories[catIndex],
        bullets: categories[catIndex].bullets.filter((_, i) => i !== bulletIndex),
      };
      return { ...prev, categories };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...form,
        categories: form.categories.map((item, i) => ({
          ...item,
          bullets: (item.bullets || []).filter(Boolean),
          order: i,
        })),
      };
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: payload, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Documents section saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded documents section defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Help Guide Section"
        description="Edit the Documents section title, intro, and category cards on the Help page."
        icon={FaFolder}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms/help')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="space-y-4">
        <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
            Published
          </label>
          <input className="input-modern" value={form.title} onChange={(e) => updateField('title', e.target.value)} placeholder="Section title" />
          <PortalIconPicker value={form.icon} onChange={(icon) => updateField('icon', icon)} />
          <textarea className="input-modern min-h-[70px]" value={form.intro} onChange={(e) => updateField('intro', e.target.value)} placeholder="Intro text" />
        </div>

        <div className="flex justify-between items-center">
          <h3 className="font-bold">Document Categories</h3>
          <button type="button" onClick={addCategory} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add Category</button>
        </div>

        {form.categories.map((cat, index) => (
          <div key={`doc-${index}`} className="rounded-2xl border p-5 space-y-3 bg-gray-50/50 dark:bg-dark-900/30">
            <div className="flex justify-between">
              <span className="text-sm font-bold">Category {index + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => moveListItem(index, -1)} disabled={index === 0} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
                <button type="button" onClick={() => moveListItem(index, 1)} disabled={index === form.categories.length - 1} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
                <button type="button" onClick={() => removeCategory(index)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><FaTrash className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="input-modern" value={cat.title} onChange={(e) => updateList(index, 'title', e.target.value)} placeholder="Category title" />
              <select className="input-modern" value={cat.color} onChange={(e) => updateList(index, 'color', e.target.value)}>
                {DOC_COLOR_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-gray-500">Bullet Points</label>
              {(cat.bullets || []).map((bullet, bi) => (
                <div key={`bullet-${bi}`} className="flex gap-2">
                  <input className="input-modern flex-1" value={bullet} onChange={(e) => updateBullet(index, bi, e.target.value)} />
                  <button type="button" onClick={() => removeBullet(index, bi)} className="p-2 text-red-500"><FaTrash className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => addBullet(index)} className="text-xs text-primary-600">+ Add bullet</button>
            </div>
          </div>
        ))}
      </div>
    </PageLayout>
  );
};

export default HelpDocumentsSectionCMS;
